import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import { createMerchantsDataManager } from '../../../services/merchants/merchantsDataManager';
import { MerchantsGrid } from '../components/MerchantsGrid';

export function MerchantsListPage(): React.ReactElement {
  useDocumentTitle('Merchants · Triplicity Backoffice');
  const { getAccessToken, acquireAccessToken } = useAuth();
  const [gridError, setGridError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  const dataManager = useMemo(
    () => createMerchantsDataManager(getAccessToken),
    [getAccessToken]
  );

  useEffect(() => {
    if (!dataManager) {
      return;
    }
    void acquireAccessToken();
  }, [dataManager, acquireAccessToken]);

  if (!dataManager) {
    return (
      <div>
        <PageHeader
          title="Merchants"
          subtitle="Browse merchant accounts connected to the 3PL."
        />
        <EmptyState
          title="API base URL is not configured"
          description="Set REACT_APP_API_BASE_URL in .env.local, or uncomment API_BASE_URL and the matching return in src/config/api.ts. Then adjust MERCHANTS_ODATA_PATH if your OData route differs."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Merchants"
        subtitle="Server-side paging, sorting, and filtering via OData. Row click opens details."
      />
      {gridError ? <ErrorAlert message={gridError} /> : null}
      <MerchantsGrid
        dataManager={dataManager}
        onGridError={setGridError}
        onEmptyChange={setIsEmpty}
      />
      {isEmpty && !gridError ? (
        <p className="bo-muted" style={{ marginTop: '0.75rem', color: '#64748b' }}>
          No rows returned. If you expected data, verify the OData path and API permissions.
        </p>
      ) : null}
      <p style={{ marginTop: '1rem' }}>
        <Link to="/">Back to home</Link>
      </p>
    </div>
  );
}
